<template>
    <article class="relative h-full w-full">
        <BaseCartButton :product />

        <div
            class="group relative h-full gap-3 overflow-hidden rounded-12 bg-[#FFFFFF0D] p-3 transition-colors duration-500 focus:bg-white/15 max-md:flex">
            <NuxtLink
                :to="link"
                target="_blank"
                :aria-label="productLinkLabel"
                class="absolute inset-0 z-10 size-full" />

            <div
                v-if="shouldRenderDesktop"
                ref="imageSwiper"
                class="swiper swiper-equipment-images relative !z-10 max-md:!hidden"
                @mouseleave="onMouseLeave"
                @mousemove="onMouseMove">
                <NuxtLink
                    :to="link"
                    target="_blank"
                    :aria-label="productLinkLabel"
                    class="absolute inset-0 z-10 size-full" />
                <div class="swiper-wrapper">
                    <div
                        v-for="(image, index) in product.images"
                        :key="image.alt || index"
                        style="transition-property: opacity"
                        class="swiper-slide">
                        <div
                            class="swiper-slide__inner w-full max-w-[16rem] overflow-hidden rounded-12 md:max-w-[26rem]">
                            <picture class="size-full object-cover">
                                <source
                                    v-if="image.webp_url"
                                    type="image/webp"
                                    :srcset="
                                        getMediaLink(
                                            image.webp_url || '',
                                            600
                                        ) || ''
                                    " />
                                <source
                                    v-else-if="!image.url && !image.webp_url"
                                    type="image/webp"
                                    srcset="
                                        /assets/images/ui/placeholder/webp/cromi.webp
                                    " />
                                <img
                                    loading="lazy"
                                    :width="image.width || 416"
                                    :height="image.height || 234"
                                    :alt="image.alt || product.name"
                                    :src="
                                        getMediaLink(image.url || '', 600) ||
                                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                                    "
                                    class="size-full object-cover" />
                            </picture>
                        </div>
                    </div>
                    <div
                        v-if="!product.images"
                        class="swiper-slide">
                        <div
                            class="swiper-slide__inner w-full max-w-[16rem] overflow-hidden rounded-12 md:max-w-[26rem]">
                            <picture>
                                <source
                                    type="image/webp"
                                    srcset="
                                        /assets/images/ui/placeholder/webp/cromi.webp
                                    " />
                                <img
                                    loading="lazy"
                                    width="416"
                                    height="234"
                                    :alt="product.name"
                                    src="/assets/images/ui/placeholder/jpg-png/cromi.png"
                                    class="aspect-square size-full object-cover" />
                            </picture>
                        </div>
                    </div>
                </div>
                <div
                    v-if="product.images && product.images.length > 1"
                    class="swiper-pagination absolute bottom-2.5 left-0 z-30 flex h-2 w-full items-center justify-between gap-2 px-2.5" />
            </div>
            <picture
                v-if="product.images && product.images.length"
                class="flex h-full max-h-25 w-full max-w-25 shrink-0 rounded-12 object-cover md:hidden">
                <source
                    v-if="product.images[0].webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(product.images[0].webp_url || '', 400) ||
                        ''
                    " />
                <source
                    v-else-if="
                        !product.images[0].webp_url && !product.images[0].url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />
                <img
                    :width="product.images[0].width || 416"
                    :height="product.images[0].height || 234"
                    :alt="product.images[0].alt || product.name"
                    :src="
                        getMediaLink(product.images[0].url || '', 400) ||
                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="flex h-full max-h-25 w-full max-w-25 shrink-0 rounded-12 object-cover md:hidden" />
            </picture>
            <picture
                v-else
                class="flex h-full max-h-25 w-full max-w-25 shrink-0 rounded-12 object-cover md:hidden">
                <source
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />
                <img
                    width="416"
                    height="234"
                    :alt="product.name"
                    src="/assets/images/ui/placeholder/jpg-png/cromi.png"
                    class="flex h-full max-h-25 w-full max-w-25 shrink-0 rounded-12 object-cover md:hidden" />
            </picture>

            <div
                class="flex flex-col justify-between max-md:flex max-md:w-full">
                <h3
                    v-html="product.name"
                    class="line-clamp-3 text-16/120 font-semibold text-ash-200 sm:text-20/120 md:mt-5 md:text-24/133" />
                <div
                    class="relative w-full text-20/120 font-medium text-accent-100 md:mt-4 md:text-16/150">
                    <div
                        class="max-w-full overflow-hidden transition-all duration-300 md:group-hover:-translate-x-[200%]">
                        <div class="flex gap-2 max-md:justify-end">
                            <p
                                v-if="product.old_price"
                                v-html="formatPrice(product.old_price)"
                                class="text-accent-100/50 line-through max-md:text-16/150" />
                            <p
                                v-html="formatPrice(product.price)"
                                class="max-md:text-16/150" />
                        </div>
                        <div
                            v-html="product.preview_description"
                            class="tinyeditor mt-2 max-xl:line-clamp-4 max-md:hidden md:mt-4" />
                    </div>
                    <div
                        v-html="product.specifies"
                        class="tinyeditor absolute top-0 hidden w-full overflow-visible transition-all duration-300 md:left-[120%] md:block md:group-hover:left-0" />
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { useMediaQuery, useMounted } from '@vueuse/core'
import { Swiper } from 'swiper'
import 'swiper/css'
import { EffectFade, Pagination } from 'swiper/modules'

import BaseCartButton from '~/components/base/base-cart-button/BaseCartButton.vue'
import type { IProductCard } from '~/components/particles/product-card/product-card.types'

import { useCartProductLimits } from '~/composables/useCartProductLimits'

const props = defineProps({
    product: {
        type: Object as PropType<IProductCard>,
        required: true,
    },
})

const { applyProductLimit } = useCartProductLimits()

const isMounted = useMounted()
const isDesktop = useMediaQuery('(min-width: 768px)')
const shouldRenderDesktop = computed(() => isMounted.value && isDesktop.value)
const slider = ref<Swiper | null>(null)
const isFadeSlider = ref(true)

const imageSwiper = ref<HTMLElement | null>(null)

const link = computed(() => {
    if (isRadioguide()) {
        return props.product.slug
    }
    return `/catalog/${props.product.slug}`
})

const productName = computed(() => stripHtml(props.product.name))
const productLinkLabel = computed(() => {
    return productName.value
        ? `Открыть страницу товара ${productName.value}`
        : 'Открыть страницу товара'
})

watch(
    () => [props.product?.slug, props.product?.max_cart_quantity],
    () => {
        applyProductLimit(props.product)
    },
    {
        immediate: true,
    }
)

function onMouseMove(event: MouseEvent): void {
    const imageSwiperElement = imageSwiper.value

    if (!slider.value || !imageSwiperElement) return

    const swiper = Array.isArray(slider.value)
        ? slider.value.find((item) => item.hostEl === imageSwiperElement)
        : slider.value

    if (swiper && isFadeSlider.value) {
        const slideIndex = Array.from<HTMLElement>(
            swiper.pagination.el.children as HTMLCollectionOf<HTMLElement>
        ).findIndex((bullet, index) => {
            const containerStart = imageSwiperElement.getBoundingClientRect().x
            const mouseX = event.clientX - containerStart - 10
            const { width } = bullet.getBoundingClientRect()
            const bulletStart = (width + 8) * index

            return mouseX >= bulletStart && mouseX <= bulletStart + width
        })

        if (slideIndex >= 0) {
            swiper.slideTo(slideIndex)
            setTimeout(() => {
                swiper.pagination.render()
                swiper.pagination.update()
            }, 0)
        }
    }
}

function onMouseLeave(): void {
    if (!slider.value) return

    const swiper = Array.isArray(slider.value)
        ? slider.value.find((item) => item.hostEl === imageSwiper.value)
        : slider.value

    if (swiper && isFadeSlider.value) {
        swiper.slideTo(0)
        setTimeout(() => {
            swiper.pagination.render()
            swiper.pagination.update()
        }, 0)
    }
}

onMounted(() => {
    if (!imageSwiper.value) return

    slider.value = new Swiper(imageSwiper.value, {
        modules: [Pagination, EffectFade],
        effect: 'fade',
        fadeEffect: {
            crossFade: true,
        },
        speed: 300,
        slidesPerView: 1,
        pagination: {
            el:
                imageSwiper.value.querySelector<HTMLElement>(
                    '.swiper-pagination'
                ) || undefined,
            horizontalClass: 'pointer-events-none',
            bulletClass:
                'block h-0.5 rounded-20 w-full bg-ash-45 transition-all duration-300',
            bulletActiveClass: '!bg-accent',
        },
    })
})

onBeforeUnmount(() => {
    slider.value?.destroy()
})
</script>
